import { useEffect } from 'react';
import { HubConnectionState } from '@microsoft/signalr';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/auth/auth-store';
import { createHubConnection } from '@/shared/realtime/signalr-connection';
import {
  NOTIFICATIONS_SIGNALR_HUB,
  NOTIFICATIONS_SIGNALR_METHOD,
} from '@/notifications/lib/notification-constants';
import { UNREAD_NOTIFICATION_COUNT_KEY } from './use-unread-notification-count';

/**
 * Live unread-count updates via SignalR.
 *
 * Hub: `/api/notifications/NotificationsHub`
 * Method: `NotificationCountChanged`
 * Payload: `{ userId: string, count: number }`
 *
 * Mirrors Flutter `menu_widget.dart:75-96` — connects on auth, listens
 * for the `NotificationCountChanged` event, filters by current user's
 * `userId`, and patches local state.
 *
 * **Filter is non-optional**: the hub broadcasts events for every user
 * in the tenant (server-side fan-out is by org, not by user). Without
 * the userId filter, a different user's notification would update OUR
 * badge.
 *
 * Cache patch: `qc.setQueryData(['notifications', 'unread-count'], count)`
 * — the badge consumer reads from this query key, so the patch updates
 * the Bell visually within one render cycle.
 *
 * Lifecycle:
 *   - Connects after auth (waits for a non-null user via auth-store)
 *   - Auto-reconnects via @microsoft/signalr on transient drops
 *   - Disconnects on logout (user goes null) and on unmount
 *
 * Same pattern as `useBillingRealtime` — see that hook for the
 * canonical StrictMode-safe start/stop choreography.
 */

export interface NotificationCountChangedPayload {
  userId: string;
  count: number;
}

export function useNotificationCountRealtime(): void {
  const userId = useAuthStore((s) => s.user?.userId);
  const qc = useQueryClient();

  useEffect(() => {
    if (!userId) return;

    const getToken = () => useAuthStore.getState().getValidAccessToken();
    const hub = createHubConnection(NOTIFICATIONS_SIGNALR_HUB, getToken);

    hub.on(NOTIFICATIONS_SIGNALR_METHOD, (...args: unknown[]) => {
      const payload = firstPayload(args);
      if (!payload) return;
      // Server-side fan-out is by org, not by user — every user in the
      // tenant gets the event. Filter to our own userId per Flutter
      // menu_widget.dart:91 (`if (userId == widget.userData!.userId)`).
      if (payload.userId !== userId) return;
      qc.setQueryData(UNREAD_NOTIFICATION_COUNT_KEY, payload.count);
    });

    let disposed = false;

    const startSafely = async () => {
      try {
        if (hub.state === HubConnectionState.Disconnected) {
          await hub.start();
        }
        if (disposed && hub.state !== HubConnectionState.Disconnected) {
          await hub.stop();
        }
      } catch {
        // Auto-reconnect handles real failures; StrictMode-induced
        // aborts are expected.
      }
    };

    void startSafely();

    return () => {
      disposed = true;
      if (hub.state === HubConnectionState.Connected) {
        void hub.stop().catch(() => {});
      }
    };
  }, [userId, qc]);
}

/**
 * SignalR `.on(method, callback)` may invoke the callback with the
 * payload as either a single positional arg or as an array — handle both.
 * Mirrors the helper in `use-billing-realtime.ts:138-143`.
 */
function firstPayload(
  args: unknown[],
): NotificationCountChangedPayload | undefined {
  const head = args[0];
  if (Array.isArray(head))
    return head[0] as NotificationCountChangedPayload | undefined;
  if (head && typeof head === 'object')
    return head as NotificationCountChangedPayload;
  return undefined;
}
